import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';

// Components
import Navbar from './components/Navbar';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider } from './contexts/AuthContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { LanguageProvider } from './contexts/LanguageContext';

// Pages
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import RestaurantDashboard from './pages/RestaurantDashboard';
import StaffDashboard from './pages/StaffDashboard';
import MenuManagement from './pages/MenuManagement';
import TableManagement from './pages/TableManagement';
import OrderManagement from './pages/OrderManagement';
import StaffOrderManagement from './pages/StaffOrderManagement'; 
import CustomerMenu from './pages/CustomerMenu';
import Pricing from './pages/Pricing';
import About from './pages/About';
import Contact from './pages/Contact';
import Profile from './pages/Profile';
import StaffManagement from './pages/StaffManagement';
import Subscription from './pages/Subscription';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import SubscriptionCancel from './pages/SubscriptionCancel';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import PasswordResetConfirm from './pages/PasswordResetConfirm';
import SimpleStatistics from './pages/SimpleStatistics';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <AuthProvider>
          <SubscriptionProvider>
            <Router
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
              }}
            >
              <div className="App">
                <Navbar />
                <main>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/customer/:qrCode" element={<CustomerMenu />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/password-reset-confirm" element={<PasswordResetConfirm />} />

                    {/* Protected Routes */}
                    {/* Owner Dashboard - Only for owners */}
                    <Route path="/dashboard" element={
                      <ProtectedRoute ownerOnly={true}>
                        <RestaurantDashboard />
                      </ProtectedRoute>
                    } />

                    {/* Staff Dashboard - Only for staff */}
                    <Route path="/staff-dashboard" element={
                      <ProtectedRoute staffOnly={true}>
                        <StaffDashboard />
                      </ProtectedRoute>
                    } />

                    {/* Owner-only management routes */}
                    <Route path="/staff" element={
                      <ProtectedRoute ownerOnly={true}>
                        <StaffManagement />
                      </ProtectedRoute>
                    } />
                    <Route path="/subscription" element={
                      <ProtectedRoute ownerOnly={true}>
                        <Subscription />
                      </ProtectedRoute>
                    } />

                    <Route path="/subscription/success" element={
                      <ProtectedRoute>
                        <SubscriptionSuccess />
                      </ProtectedRoute>
                    } />
                    <Route path="/subscription/cancel" element={
                      <ProtectedRoute>
                        <SubscriptionCancel />
                      </ProtectedRoute>
                    } />
                    <Route path="/SimpleStatistics" element={
                      <ProtectedRoute>
                        <SimpleStatistics />
                      </ProtectedRoute>
                    } />

                    {/* Shared routes for both owners and staff */}
                    <Route path="/menu" element={
                      <ProtectedRoute allowedRoles={['owner', 'staff']}>
                        <MenuManagement />
                      </ProtectedRoute>
                    } />
                    <Route path="/tables" element={
                      <ProtectedRoute allowedRoles={['owner', 'staff']}>
                        <TableManagement />
                      </ProtectedRoute>
                    } />

                    {/* Separate Order Management for Owners and Staff */}
                    <Route path="/orders" element={
                      <ProtectedRoute ownerOnly={true}>
                        <OrderManagement />
                      </ProtectedRoute>
                    } />
                    <Route path="/staff-orders" element={
                      <ProtectedRoute staffOnly={true}>
                        <StaffOrderManagement />
                      </ProtectedRoute>
                    } />

                    <Route path="/profile" element={
                      <ProtectedRoute allowedRoles={['owner', 'staff']}>
                        <Profile />
                      </ProtectedRoute>
                    } />
                  </Routes>
                </main>
                <Toaster
                  position="top-right"
                  reverseOrder={false}
                  gutter={8}
                  containerClassName=""
                  containerStyle={{}}
                  toastOptions={{
                    duration: 4000,
                    className: '',
                    style: {
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: '#fff',
                      borderRadius: '12px',
                      padding: '16px 20px',
                      fontSize: '14px',
                      fontWeight: '500',
                      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2), 0 4px 10px rgba(0, 0, 0, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(10px)',
                      maxWidth: '400px',
                      minWidth: '300px',
                    },
                    success: {
                      duration: 3000,
                      style: {
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: '#fff',
                        borderRadius: '12px',
                        padding: '16px 20px',
                        fontSize: '14px',
                        fontWeight: '500',
                        boxShadow: '0 10px 25px rgba(16, 185, 129, 0.3), 0 4px 10px rgba(16, 185, 129, 0.1)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        backdropFilter: 'blur(10px)',
                      },
                      iconTheme: {
                        primary: '#fff',
                        secondary: '#10b981',
                      },
                    },
                    error: {
                      duration: 5000,
                      style: {
                        background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                        color: '#fff',
                        borderRadius: '12px',
                        padding: '16px 20px',
                        fontSize: '14px',
                        fontWeight: '500',
                        boxShadow: '0 10px 25px rgba(239, 68, 68, 0.3), 0 4px 10px rgba(239, 68, 68, 0.1)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        backdropFilter: 'blur(10px)',
                      },
                      iconTheme: {
                        primary: '#fff',
                        secondary: '#ef4444',
                      },
                    },
                    loading: {
                      duration: Infinity,
                      style: {
                        background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                        color: '#fff',
                        borderRadius: '12px',
                        padding: '16px 20px',
                        fontSize: '14px',
                        fontWeight: '500',
                        boxShadow: '0 10px 25px rgba(59, 130, 246, 0.3), 0 4px 10px rgba(59, 130, 246, 0.1)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        backdropFilter: 'blur(10px)',
                      },
                      iconTheme: {
                        primary: '#fff',
                        secondary: '#3b82f6',
                      },
                    },
                  }}
                />
              </div>
            </Router>
          </SubscriptionProvider>
        </AuthProvider>
      </LanguageProvider>
    </QueryClientProvider>
  );
}

export default App;